import { type StructuredText as StructuredTextType } from 'datocms-structured-text-utils'
import { graphql, type HeadProps, type PageProps } from 'gatsby'
import React from 'react'

import Button from '@/components/button/button'
import Newsletter from '@/components/centered-call-to-action/centered-call-to-action'
import HeaderBackgroundV2 from '@/components/header/header-background-v2'
import Image from '@/components/image/image'
import Related from '@/components/related/related'
import { useSiteMetadata } from '@/components/root-element'
import { Microdata } from '@/components/seo/microdata'
import Seo from '@/components/seo/seo'
import StructuredTextDefault from '@/components/structured-text/structured-text-default'
import Tags from '@/components/tags/tags'
import Layout from '@/layouts/layout'
import { sortRelatedContent } from '@/utils/sort'
import { generateUrlFromNode } from '@/utils/url'

import * as styles from './project.module.scss'

export type DataProps = {
  allDatoCmsPctPost: Queries.ProjectQuery['allDatoCmsPctPost']
  allDatoCmsPctInterview: Queries.ProjectQuery['allDatoCmsPctInterview']
  allDatoCmsPctPaper: Queries.ProjectQuery['allDatoCmsPctPaper']
  allDatoCmsPctDialog: Queries.ProjectQuery['allDatoCmsPctDialog']
  allDatoCmsPctProject: Queries.ProjectQuery['allDatoCmsPctProject']
  allDatoCmsPctEpisode: Queries.ProjectQuery['allDatoCmsPctEpisode']
  allDatoCmsPctPage: Queries.ProjectQuery['allDatoCmsPctPage']
  allDatoCmsPctExternalContent: Queries.ProjectQuery['allDatoCmsPctExternalContent']
  allDatoCmsPctEvent: Queries.ProjectQuery['allDatoCmsPctEvent']
  datoCmsPctProject: Queries.ProjectQuery['datoCmsPctProject']
}

export const Head: React.FC<HeadProps<DataProps>> = ({
  data: { datoCmsPctProject },
  location
}) => (
  <>
    <Seo data={datoCmsPctProject} location={location} />
    <Microdata data={datoCmsPctProject} location={location} />
  </>
)

const Project: React.FC<PageProps<DataProps>> = ({
  data: {
    datoCmsPctProject,
    allDatoCmsPctPost,
    allDatoCmsPctInterview,
    allDatoCmsPctPaper,
    allDatoCmsPctDialog,
    allDatoCmsPctEpisode,
    allDatoCmsPctProject,
    allDatoCmsPctPage,
    allDatoCmsPctExternalContent,
    allDatoCmsPctEvent
  },
  location
}) => {
  const { settings } = useSiteMetadata()
  if (!datoCmsPctProject) {
    return <></>
  }
  const {
    externalName,
    client,
    country,
    areas,
    clientNeed,
    howDid,
    result,
    lineOfBusiness,
    headline,
    topic,
    tags,
    image
  } = datoCmsPctProject
  const related = sortRelatedContent({
    results: 3,
    nodesGroups: [
      allDatoCmsPctProject.nodes as Card[],
      [
        ...(allDatoCmsPctPost.nodes ?? []),
        ...(allDatoCmsPctInterview.nodes ?? []),
        ...(allDatoCmsPctPaper.nodes ?? []),
        ...(allDatoCmsPctDialog.nodes ?? []),
        ...(allDatoCmsPctEpisode.nodes ?? []),
        ...(allDatoCmsPctPage.nodes ?? []),
        ...(allDatoCmsPctExternalContent.nodes ?? []),
        ...(allDatoCmsPctEvent.nodes ?? [])
      ]
    ]
  })

  const {
    'project-detail-client-needs': projectDetailClientNeeds,
    'project-detail-our-solution': projectDetailOurSolution,
    'project-detail-results-benefits': projectDetailResultsBenefits,
    'project-detail-client': projectDetailClient,
    'project-detail-digital-focus-area': projectDetailDigitalFocusArea,
    'project-detail-country': projectDetailCountry,
    'project-detail-line-of-business': projectDetailLineOfBusiness
  } = settings

  return (
    <Layout location={location}>
      <div className={styles.projectDetail}>
        <HeaderBackgroundV2
          image={image}
          title={externalName ?? ''}
          headline={headline ?? ''}
        />
        <div className="container">
          <div className="row mt-lg-5 mb-lg-4 mt-1 mb-3 justify-content-between">
            <div className="col-xxl-8 col-lg-8 order-1 order-lg-0">
              <div className="row justify-content-between">
                <div
                  className={`${styles.colProjectInfo} col-lg-12 mb-4 mt-4 mt-lg-0`}
                >
                  <span>01</span>
                  <h2>{projectDetailClientNeeds as string}</h2>
                  <StructuredTextDefault
                    content={clientNeed as unknown as StructuredTextType}
                  />
                </div>
                <div className={`${styles.colProjectInfo} col-lg-12 mb-4`}>
                  <span>02</span>
                  <h2>{projectDetailOurSolution as string}</h2>
                  <StructuredTextDefault
                    content={howDid as unknown as StructuredTextType}
                  />
                </div>
                <div className={`${styles.colProjectInfo} col-lg-12 mb-4`}>
                  <span>03</span>
                  <h2>{projectDetailResultsBenefits as string}</h2>
                  <StructuredTextDefault
                    content={result as unknown as StructuredTextType}
                  />
                </div>
              </div>
            </div>
            <div className="col-xxl-3 col-lg-4 mt-lg-5 order-0 order-lg-1">
              <div className={styles.infoProject}>
                <div className="container">
                  <div className="row">
                    {client?.[0] && (
                      <Client
                        items={client}
                        title={projectDetailClient as string}
                      />
                    )}
                    <Areas
                      items={areas}
                      title={projectDetailDigitalFocusArea as string}
                    />
                    <Country
                      items={country}
                      title={projectDetailCountry as string}
                    />
                    <LineOfBusiness
                      items={lineOfBusiness}
                      title={projectDetailLineOfBusiness as string}
                    />

                    {topic && (
                      <div className={styles.ctaTopic}>
                        <p className={styles.label}>Topic</p>

                        <Button
                          color="tertiary"
                          title={topic.externalName}
                          to={generateUrlFromNode(topic)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Tags tags={tags} />
        </div>
      </div>

      <Related related={related} />
      <Newsletter />
    </Layout>
  )
}

type ClientProps = {
  items:
    | readonly ({
        readonly id: string
        readonly externalName: string | null
        readonly logo: {
          readonly url: string | null
          readonly width: number | null
          readonly height: number | null
        } | null
      } | null)[]
    | null
  title: string
}
const Client: React.FC<ClientProps> = ({ items, title }) => {
  return (
    <div className={`col-12 ${styles.companyItem}`}>
      <p className={styles.label}>{title}</p>
      <ul>
        {items?.map((item) => {
          if (!item) {
            return null
          }
          const { externalName, logo, id } = item
          return (
            <li
              key={`${id}-${externalName}`}
              className={`${logo?.url ? styles.image : 'no-image'}`}
            >
              {/* eslint-disable-next-line */}
              {logo?.url && logo?.width && logo?.height && (
                <img
                  src={logo.url}
                  alt={externalName ?? ''}
                  className={(() => {
                    const aspectRatio = logo.width / logo.height
                    if (aspectRatio > 1.3) {
                      return styles.horizontal ?? 'horizontal'
                    }
                    if (aspectRatio < 0.8) {
                      return styles.vertical ?? 'vertical'
                    }
                    return styles.square ?? 'square'
                  })()}
                />
              )}
              <p>{externalName}</p>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

type CountryProps = {
  items:
    | readonly ({
        readonly id: string
        readonly externalName: string | null
      } | null)[]
    | null
  title: string
}

const Country: React.FC<CountryProps> = ({ items, title }) => {
  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className="col-12">
      <p className={styles.label}>{title}</p>
      <ul>
        {items.map(
          (item) =>
            item && (
              <li className="no-image" key={item.externalName}>
                {item.externalName}
              </li>
            )
        )}
      </ul>
    </div>
  )
}

type AreasProps = {
  items:
    | readonly ({
        readonly id: string
        readonly externalName: string | null
        readonly icon: {
          readonly url: string | null
          readonly gatsbyImageData: Record<string, unknown> | null
        } | null
      } | null)[]
    | null
  title: string
}

const Areas: React.FC<AreasProps> = ({ items, title }) => {
  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className={`col-12 ${styles.linesWithImage}`}>
      <p className={styles.label}>{title}</p>
      <ul>
        {items.map(
          (item) =>
            item && (
              <li className="no-image" key={item.externalName}>
                {item.icon && <Image image={item.icon} />}
                {item.externalName}
              </li>
            )
        )}
      </ul>
    </div>
  )
}

type LineOfBusinessProps = {
  items:
    | readonly ({
        readonly id: string
        readonly externalName: string | null
        readonly icon: {
          readonly url: string | null
          readonly gatsbyImageData: Record<string, unknown> | null
        } | null
      } | null)[]
    | null
  title: string
}

const LineOfBusiness: React.FC<LineOfBusinessProps> = ({ items, title }) => {
  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className="col-12 lines-with-image">
      <p className={styles.label}>{title}</p>
      <ul>
        {items.map(
          (item) =>
            item && (
              <li key={item.externalName}>
                {item.icon && <Image image={item.icon} />}
                {item.externalName}
              </li>
            )
        )}
      </ul>
    </div>
  )
}
export default Project

export const query = graphql`
  query Project($id: String, $related: [String] = "") {
    datoCmsPctProject(id: { eq: $id }) {
      ...ProjectDetail
    }
    allDatoCmsPctPaper(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPaperCard
      }
    }
    allDatoCmsPctPage(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPageCard
      }
    }
    allDatoCmsPctPost(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPostCard
      }
    }
    allDatoCmsPctInterview(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctInterviewCard
      }
    }
    allDatoCmsPctDialog(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctDialogCard
      }
    }
    allDatoCmsPctEpisode(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEpisodeCard
      }
    }
    allDatoCmsPctProject(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctProjectCard
      }
    }
    allDatoCmsPctExternalContent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related }, showInWebsite: { eq: true } }
    ) {
      nodes {
        ...PctExternalContentCard
      }
    }
    allDatoCmsPctEvent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEventCard
      }
    }
  }
  fragment ProjectDetail on DatoCmsPctProject {
    __typename
    originalId
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    seo {
      title
      description
      image {
        url
      }
    }
    id
    meta {
      firstPublishedAt(formatString: "MMMM D, YYYY")
      publishedAt(formatString: "DD/MM/YYYY")
      publishedAtTimestamp: publishedAt(formatString: "X")
    }
    externalName
    headline
    image {
      gatsbyImageData(
        imgixParams: { ar: "16:9", fit: "crop", crop: "focalpoint" }
      )
      alt
    }
    card: image {
      ...imgCard
    }
    clientNeed {
      value
    }
    result {
      value
    }
    howDid {
      value
    }
    client {
      id
      externalName
      logo {
        url
        width
        height
      }
    }
    areas {
      id
      externalName
      icon {
        url
        gatsbyImageData
      }
    }
    country {
      id
      externalName
    }
    lineOfBusiness {
      id
      externalName
      icon {
        url
        gatsbyImageData
      }
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
  }
`
